import React from "react";
import { Download, Moon, Plus, Redo2, Save, Settings, Sun, Undo2, ZoomIn } from "lucide-react";

type TopBarProps = {
  structureName?: string;
  structureDescription?: string;
  saveToneClass: string;
  saveStateLabel: string;
  lastSavedAt: string | null;
  wsConnected: boolean;
  wsPresence: number;
  viewOnly: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undoHint?: string | null;
  onUndo: () => void;
  onRedo: () => void;
  onFitView: () => void;
  onOpenContacts: () => void;
  onAddNode: () => void;
  newNodeType: string;
  nodeOptions: { id: string; label: string }[];
  onNewNodeTypeChange: (value: string) => void;
  onSave: () => void;
  saving: boolean;
  onOpenSettings: () => void;
  onTogglePngControls?: () => void;
  pngExporting?: boolean;
  onToggleTheme: () => void;
  theme: "light" | "dark";
};

export function TopBar({
  structureName,
  structureDescription,
  saveToneClass,
  saveStateLabel,
  lastSavedAt,
  wsConnected,
  wsPresence,
  viewOnly,
  canUndo,
  canRedo,
  undoHint,
  onUndo,
  onRedo,
  onFitView,
  onOpenContacts,
  onAddNode,
  newNodeType,
  nodeOptions,
  onNewNodeTypeChange,
  onSave,
  saving,
  onOpenSettings,
  onTogglePngControls,
  pngExporting,
  onToggleTheme,
  theme,
}: TopBarProps) {
  return (
    <div className="flex items-center justify-between px-3 md:px-5 py-2 md:py-3 border-b border-[var(--color-border)] bg-[var(--color-panel)]/90 backdrop-blur">
      <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="hidden md:inline-flex px-2 py-1 rounded-md bg-[var(--color-panel-2)] text-[11px] font-semibold uppercase text-[var(--color-muted)]">
            Atlas
          </span>
          <div className="min-w-0">
            <div className="font-black text-sm md:text-lg leading-tight title-font truncate">{structureName || "Editor"}</div>
            {structureDescription && (
              <div className="hidden md:block text-xs text-[var(--color-muted)] subtitle-font truncate">{structureDescription}</div>
            )}
            <div className="flex items-center gap-1 md:gap-2 text-[10px] md:text-xs text-[var(--color-muted)]">
              <span className={`inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full ${saveToneClass}`}>
                {saveStateLabel}
              </span>
              {lastSavedAt && <span className="hidden sm:inline">Zuletzt {lastSavedAt}</span>}
              {wsConnected && wsPresence > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                  <span className="hidden sm:inline">{wsPresence} online</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
        {viewOnly && <span className="badge text-[10px] md:text-xs">View only</span>}
        <div className="hidden lg:flex items-center gap-1">
          <button
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onUndo}
            disabled={!canUndo}
            title={!canUndo && undoHint ? undoHint : undefined}
          >
            <Undo2 className="h-4 w-4" /> <span className="hidden xl:inline">Undo</span>
          </button>
          <button className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed" onClick={onRedo} disabled={!canRedo}>
            <Redo2 className="h-4 w-4" /> <span className="hidden xl:inline">Redo</span>
          </button>
        </div>
        <button className="btn btn-secondary" onClick={onFitView}>
          <ZoomIn className="h-4 w-4" /> <span className="hidden md:inline">Fit</span>
        </button>
        <button className="btn btn-secondary md:hidden" onClick={onOpenContacts}>
          Kontakte
        </button>
        {!viewOnly && (
          <>
            <div className="hidden md:flex items-center gap-2 bg-[var(--color-panel-2)] px-2 py-1 rounded-lg border border-[var(--color-border)]">
              <select
                className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1.5 text-xs md:text-sm"
                value={newNodeType}
                onChange={(e) => onNewNodeTypeChange(e.target.value)}
              >
                {nodeOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              <button className="btn btn-secondary" onClick={onAddNode}>
                <Plus className="h-4 w-4" /> <span className="hidden lg:inline">Node</span>
              </button>
            </div>
            <button className="btn btn-primary" onClick={onSave} disabled={saving}>
              <Save className="h-4 w-4" /> <span className="hidden md:inline">{saving ? "Speichert" : "Speichern"}</span>
            </button>
            <div className="hidden lg:flex items-center gap-2">
              <button className="btn btn-secondary" onClick={onOpenContacts}>
                Kontakte
              </button>
              {onTogglePngControls && (
                <button className="btn btn-secondary" onClick={onTogglePngControls} disabled={!!pngExporting}>
                  <Download className="h-4 w-4" /> <span className="hidden xl:inline">PNG</span>
                </button>
              )}
              <button className="btn btn-secondary" onClick={onOpenSettings}>
                <Settings className="h-4 w-4" /> <span className="hidden xl:inline">Einstellungen</span>
              </button>
            </div>
          </>
        )}
        <button className="btn btn-secondary p-2 md:px-3" onClick={onToggleTheme} aria-label="Theme toggle">
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
